import React, { useState, useEffect, useRef } from 'react';
import CarCard from '../components/CarCard';
import WavyBackground from '../components/WavyBackground';
import apiService from '../services/api';
import '../styles/CarList.css';
import { useLocation, useNavigate } from 'react-router-dom';

const kilometrageRanges = [
  { value: '0-50000', label: 'Moins de 50 000 km', min: 0, max: 50000 },
  { value: '50000-100000', label: '50 000 - 100 000 km', min: 50000, max: 100000 },
  { value: '100000+', label: 'Plus de 100 000 km', min: 100000, max: Infinity }
];

const transmissions = [
  { value: 'Manuelle', label: 'Manuelle' },
  { value: 'Automatique', label: 'Automatique' }
];

const critairOptions = [
  { value: '0', label: "Crit'Air 0 - 100% électrique" },
  { value: '1', label: "Crit'Air 1" },
  { value: '2', label: "Crit'Air 2" },
  { value: '3', label: "Crit'Air 3" }
];

const CarList = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Initialiser l'état depuis les query params pour préserver filtres/pagination
  const initialParams = new URLSearchParams(location.search);
  // Si pas de query params, tenter de restaurer depuis sessionStorage
  let initialPage = parseInt(initialParams.get('page')) || null;
  const savedStateJSON = sessionStorage.getItem('carListState');
  let savedState = null;
  try { savedState = savedStateJSON ? JSON.parse(savedStateJSON) : null; } catch (e) { savedState = null; }
  if (!initialPage && savedState && savedState.page) {
    initialPage = savedState.page;
  }
  if (!initialPage) initialPage = 1;

  const [cars, setCars] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [filters, setFilters] = useState({
    recherche: initialParams.get('recherche') || '',
    marque: initialParams.get('marque') || '',
    prixMax: initialParams.get('prixMax') || '',
    energie: initialParams.get('energie') || '',
    kilometrage: initialParams.get('kilometrage') || '',
    transmission: initialParams.get('transmission') || '',
    critair: initialParams.get('critair') || ''
  });

  const carsPerPage = window.innerWidth <= 768 ? 5 : 9;
  const indexOfLastCar = currentPage * carsPerPage;
  const indexOfFirstCar = indexOfLastCar - carsPerPage;
  const currentCars = filteredCars.slice(indexOfFirstCar, indexOfLastCar);
  const totalPages = Math.ceil(filteredCars.length / carsPerPage);

  useEffect(() => {
    loadCars();
  }, []);

  // Sync URL query params when filters or page change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.recherche) params.set('recherche', filters.recherche);
    if (filters.marque) params.set('marque', filters.marque);
    if (filters.prixMax) params.set('prixMax', filters.prixMax);
    if (filters.energie) params.set('energie', filters.energie);
    if (filters.kilometrage) params.set('kilometrage', filters.kilometrage);
    if (filters.transmission) params.set('transmission', filters.transmission);
    if (filters.critair) params.set('critair', filters.critair);
    if (currentPage && currentPage !== 1) params.set('page', currentPage);

    const queryString = params.toString();
    navigate(`${location.pathname}${queryString ? `?${queryString}` : ''}`, { replace: true });
    // Sauvegarder l'état dans sessionStorage comme fallback
    try {
      sessionStorage.setItem('carListState', JSON.stringify({ filters, page: currentPage }));
    } catch (e) {
      // ignore
    }
  }, [filters, currentPage, navigate, location.pathname]);

  const loadCars = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getCars({ disponible: 'true' });

      if (response.success) {
        setCars(response.data);
        setFilteredCars(response.data);
      } else {
        setError('Erreur lors du chargement des véhicules');
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des véhicules');
    } finally {
      setLoading(false);
    }
  };

  const hasUserChangedFilters = useRef(false);

  useEffect(() => {
    let filtered = cars;

    // Filtre par recherche de modèle
    if (filters.recherche) {
      filtered = filtered.filter(car =>
        car.modele.toLowerCase().includes(filters.recherche.toLowerCase()) ||
        car.marque.toLowerCase().includes(filters.recherche.toLowerCase())
      );
    }

    // Filtre par marque
    if (filters.marque) {
      filtered = filtered.filter(car =>
        car.marque.toLowerCase().includes(filters.marque.toLowerCase())
      );
    }

    // Filtre par prix maximum
    if (filters.prixMax) {
      filtered = filtered.filter(car => car.prix <= parseInt(filters.prixMax));
    }

    // Filtre par énergie
    if (filters.energie) {
      filtered = filtered.filter(car => car.energie === filters.energie);
    }

    // Filtre par kilométrage
    if (filters.kilometrage) {
      const range = kilometrageRanges.find(r => r.value === filters.kilometrage);
      if (range) {
        filtered = filtered.filter(car =>
          car.kilometrage >= range.min && car.kilometrage <= range.max
        );
      }
    }

    // Filtre par transmission
    if (filters.transmission) {
      filtered = filtered.filter(car => car.transmission === filters.transmission);
    }

    // Filtre par Crit'Air
    if (filters.critair) {
      filtered = filtered.filter(car => car.critAir.toString() === filters.critair);
    }

    setFilteredCars(filtered);

    // Ne reset la page que si l'utilisateur a réellement modifié les filtres.
    // Le chargement initial des données ne doit pas écraser page=2 ou autre valeur venue de l'URL.
    if (hasUserChangedFilters.current) {
      setCurrentPage(1);
    }
  }, [filters, cars]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    hasUserChangedFilters.current = true;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetFilters = () => {
    hasUserChangedFilters.current = true;
    setFilters({
      recherche: '',
      marque: '',
      prixMax: '',
      energie: '',
      kilometrage: '',
      transmission: '',
      critair: ''
    });
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Effet pour scroller après le changement de page
  useEffect(() => {
    const scrollToGrid = () => {
      const resultsInfo = document.querySelector('.results-info');
      if (resultsInfo) {
        const offset = 80;
        const topPosition = resultsInfo.offsetTop - offset;
        window.scrollTo({
          top: topPosition,
          behavior: 'smooth'
        });
      }
    };

    // Petit délai pour s'assurer que le DOM est mis à jour
    setTimeout(scrollToGrid, 100);
  }, [currentPage]); // Se déclenche quand la page change

  const uniqueMarques = [...new Set(cars.map(car => car.marque))];
  const energies = [
    { value: "Essence", label: "Essence" },
    { value: "Diesel", label: "Diesel" },
    { value: "Hybride", label: "Hybride" },
    { value: "Hybride rechargeable", label: "Hybride rechargeable" },
    { value: "Électrique", label: "Électrique" }
  ];

  // Gestion des états de chargement et d'erreur
  if (loading) {
    return (
      <div className="car-list-page">
        <section className="carlist-results-section">
          <div className="container">
            <div className="loading-message">
              <h2>Chargement des véhicules...</h2>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="car-list-page">
        <section className="carlist-results-section">
          <div className="container">
            <div className="error-message">
              <h2>Erreur</h2>
              <p>{error}</p>
              <button onClick={loadCars} className="btn btn-primary">
                Réessayer
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="car-list-page">
      {/* Section Header + Filtres */}
      <section className="carlist-header-section">
        <WavyBackground
          backgroundFill="#1a1a1a"
          colors={["#8f8e85", "#a8a26c", "#ccbe45"]}
          waveWidth={30}
          blur={0.9}
          speed="medium"
          waveOpacity={0.15}
          containerClassName="carlist-header-wavy"
        />
        <div className="container">
          <div className="page-header">
            <h1>Nos Véhicules</h1>
            <p className="page-subtitle">Découvrez notre sélection de véhicules d'occasion</p>
          </div>

          <div className="filters-mobile-header">
            <button
              className={`filter-toggle-btn ${isFilterExpanded ? 'expanded' : ''}`}
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            >
              <span className="filter-icon">🔍</span>
              Filtres
              <span className={`arrow-icon ${isFilterExpanded ? 'up' : 'down'}`}>
                {isFilterExpanded ? '▼' : '▲'}
              </span>
            </button>
          </div>
          <div className={`filters ${isFilterExpanded ? 'expanded' : ''}`}>
            <div className="filters-row">
              <div className="filter-group">
                <label htmlFor="recherche">Rechercher:</label>
                <input
                  type="text"
                  id="recherche"
                  name="recherche"
                  placeholder="Marque ou modèle..."
                  value={filters.recherche}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="filter-group">
                <label htmlFor="marque">Marque:</label>
                <select
                  id="marque"
                  name="marque"
                  value={filters.marque}
                  onChange={handleFilterChange}
                >
                  <option value="">Toutes</option>
                  {uniqueMarques.map(marque => (
                    <option key={marque} value={marque}>{marque}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="prixMax">Prix maximum:</label>
                <input
                  type="number"
                  id="prixMax"
                  name="prixMax"
                  placeholder="Ex: 20000"
                  value={filters.prixMax}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="filter-group">
                <label htmlFor="energie">Énergie:</label>
                <select
                  id="energie"
                  name="energie"
                  value={filters.energie}
                  onChange={handleFilterChange}
                >
                  <option value="">Toutes</option>
                  {energies.map(energie => (
                    <option key={energie.value} value={energie.value}>
                      {energie.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="kilometrage">Kilométrage:</label>
                <select
                  id="kilometrage"
                  name="kilometrage"
                  value={filters.kilometrage}
                  onChange={handleFilterChange}
                >
                  <option value="">Tous</option>
                  {kilometrageRanges.map(range => (
                    <option key={range.value} value={range.value}>
                      {range.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="transmission">Transmission:</label>
                <select
                  id="transmission"
                  name="transmission"
                  value={filters.transmission}
                  onChange={handleFilterChange}
                >
                  <option value="">Toutes</option>
                  {transmissions.map(trans => (
                    <option key={trans.value} value={trans.value}>
                      {trans.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="critair">Crit'Air:</label>
                <select
                  id="critair"
                  name="critair"
                  value={filters.critair}
                  onChange={handleFilterChange}
                >
                  <option value="">Tous</option>
                  {critairOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <button onClick={resetFilters} className="btn btn-outline reset-btn">
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section Résultats */}
      <section className="carlist-results-section">
        <div className="container">
          <div className="results-info">
            <div className="results-count">
              <span className="count-number">{filteredCars.length}</span>
              <span className="count-text">véhicule(s) trouvé(s)</span>
            </div>
            {totalPages > 1 && (
              <div className="pagination-info">
                Page {currentPage} sur {totalPages}
              </div>
            )}
          </div>

          {/* Liste des voitures */}
          <div className="cars-grid">
            {currentCars.length > 0 ? (
              currentCars.map(car => (
                <CarCard key={car.id} car={car} currentSearch={location.search} />
              ))
            ) : (
              <div className="no-results">
                <div className="no-results-icon">🔍</div>
                <h3>Aucun véhicule trouvé</h3>
                <p>Aucun véhicule ne correspond à vos critères de recherche.</p>
                <button onClick={resetFilters} className="btn btn-primary">
                  Voir tous les véhicules
                </button>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn pagination-prev"
              >
                ←
              </button>

              <div className="pagination-numbers">
                <span className="pagination-info">
                  Page <span className="current-page">{currentPage}</span> sur <span className="total-pages">{totalPages}</span>
                </span>
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn pagination-next"
              >
                →
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CarList;
